import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const renderQuick = (body, first, shop) =>
  (body || "")
    .replaceAll("{name}", first || "there")
    .replaceAll("{first}", first || "")
    .replaceAll("{shop}", shop || "our shop")
    .replace(/\s{2,}/g, " ")
    .trim();

export default function MobileMessages() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [shopName, setShopName] = useState("");
  const endRef = useRef(null);

  const loadThreads = useCallback(() => {
    api
      .get("/sms/threads")
      .then(({ data }) => setThreads(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);
  useEffect(() => {
    api
      .get("/sms/quick-replies")
      .then(({ data }) => {
        setQuickReplies(data.items || []);
        setShopName(data.shop_name || "");
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (active && endRef.current) endRef.current.scrollIntoView({ block: "end" });
  }, [msgs, active]);

  const loadThread = async (phone) => {
    const { data } = await api.get("/sms/thread", { params: { phone } });
    setMsgs(data.items || []);
    setActive((a) => ({
      ...(a || {}),
      phone,
      customer_id: data.customer_id || a?.customer_id || "",
      name: data.name || a?.name || "",
    }));
  };

  const openThread = async (t) => {
    setActive(t);
    setMsgs([]);
    try {
      await loadThread(t.phone);
      if (t.unread) {
        api.post("/sms/read", null, { params: { phone: t.phone } }).catch(() => {});
        setThreads((ts) =>
          ts.map((x) => (x.phone_key === t.phone_key ? { ...x, unread: 0 } : x)),
        );
      }
    } catch {
      /* ignore */
    }
  };

  const send = async (body) => {
    const b = (body || "").trim();
    if (!b || !active?.phone) return;
    setBusy(true);
    try {
      await api.post("/sms/send", {
        to: active.phone,
        body: b,
        entity: "customer",
        entity_id: active.customer_id || "",
      });
      setText("");
      await loadThread(active.phone);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not send text",
      );
    }
    setBusy(false);
  };

  // ---- Conversation view ----
  if (active) {
    return (
      <div className="flex min-h-full flex-col" data-testid="mobile-messages-thread">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-zinc-800 bg-[#080C14]/95 px-4 py-3 backdrop-blur">
          <button
            type="button"
            data-testid="mobile-messages-back"
            onClick={() => {
              setActive(null);
              loadThreads();
            }}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-800 text-zinc-300 hover:text-[#c39e6d]"
          >
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-zinc-100">
              {active.name || active.phone}
            </div>
            <div className="truncate text-[11px] text-zinc-500">{active.phone}</div>
          </div>
        </div>

        <div
          className="flex-1 space-y-2 overflow-y-auto px-4 py-4"
          data-testid="mobile-messages-msgs"
        >
          {msgs.length === 0 && (
            <p className="text-sm text-zinc-500">No messages yet.</p>
          )}
          {msgs.map((m, i) => {
            const inbound = m.direction === "inbound";
            return (
              <div
                key={m.id || i}
                data-testid={`mobile-messages-msg-${i}`}
                className={`flex ${inbound ? "justify-start" : "justify-end"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                    inbound
                      ? "rounded-bl-sm bg-zinc-800 text-zinc-100"
                      : "rounded-br-sm bg-[#c39e6d] text-slate-950"
                  }`}
                >
                  <p className="whitespace-pre-line text-sm">{m.body}</p>
                  <div
                    className={`mt-1 text-[10px] ${
                      inbound ? "text-zinc-400" : "text-[#c39e6d]/80"
                    }`}
                  >
                    {inbound ? "Customer" : "Shop"} · {relTime(m.sent_at)}
                    {m.status && m.status !== "received" && !inbound
                      ? ` · ${m.status}`
                      : ""}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="sticky bottom-0 border-t border-zinc-800 bg-[#080C14]/95 px-4 py-3 backdrop-blur">
          <div className="mb-2 flex flex-wrap gap-2">
            {quickReplies.map((q, qi) => (
              <button
                key={qi}
                type="button"
                data-testid={`mobile-messages-quick-${qi}`}
                onClick={() =>
                  send(renderQuick(q.body, (active.name || "").split(" ")[0], shopName))
                }
                disabled={busy}
                className="rounded-full border border-[#c39e6d]/40 bg-[#c39e6d]/10 px-3 py-1.5 text-xs font-semibold text-[#c39e6d] transition-colors duration-150 hover:bg-[#c39e6d]/20 disabled:opacity-40"
              >
                {q.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="mobile-messages-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send(text)}
              placeholder="Text the customer…"
              disabled={busy}
              className="h-11 min-w-0 flex-1 rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none disabled:opacity-50"
            />
            <button
              type="button"
              data-testid="mobile-messages-send"
              onClick={() => send(text)}
              disabled={busy || !text.trim()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#c39e6d] text-slate-950 hover:bg-[#c39e6d] disabled:opacity-40"
            >
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Inbox list view ----
  return (
    <div data-testid="mobile-messages">
      <div className="px-4 pb-2 pt-5">
        <h1 className="m-title m-display text-3xl text-[#c39e6d]">Messages</h1>
      </div>
      {loading ? (
        <p className="px-4 py-6 text-sm text-zinc-500">Loading…</p>
      ) : threads.length === 0 ? (
        <p className="px-4 py-6 text-sm text-zinc-500" data-testid="mobile-messages-empty">
          No conversations yet.
        </p>
      ) : (
        <div className="divide-y divide-zinc-800">
          {threads.map((t, i) => (
            <button
              key={t.phone_key}
              type="button"
              data-testid={`mobile-msg-thread-${i}`}
              onClick={() => openThread(t)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-[#0F172A]"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-zinc-800 bg-[#0F172A] text-sm font-bold text-[#c39e6d]">
                {(t.name || t.phone || "?").trim().charAt(0).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span
                    className={`truncate text-sm ${
                      t.unread ? "font-bold text-zinc-50" : "font-semibold text-zinc-200"
                    }`}
                  >
                    {t.name || t.phone}
                  </span>
                  <span className="shrink-0 text-[11px] text-zinc-500">
                    {relTime(t.last_at)}
                  </span>
                </span>
                <span className="mt-0.5 flex items-center justify-between gap-2">
                  <span
                    className={`truncate text-xs ${
                      t.unread ? "text-zinc-300" : "text-zinc-500"
                    }`}
                  >
                    {t.last_dir === "inbound" ? "" : "You: "}
                    {t.last_body}
                  </span>
                  {t.unread > 0 && (
                    <span
                      data-testid={`mobile-msg-unread-${i}`}
                      className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                    >
                      {t.unread > 9 ? "9+" : t.unread}
                    </span>
                  )}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
